// PATH: src/pages/ObjectiveTrialsPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrowRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import FlagIcon from "@mui/icons-material/FlagOutlined";
import { useAppConfigsApi, useCurriculumApi, useObjectiveTrialsApi } from "../api";
import type { CurriculumProgram, ObjectiveTrial, ObjectiveTrialEstimate, ResolvedObjectiveRole, UUID } from "../models/domain";
import { EmptyState, PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import TrialDocumentPicker from "../components/trials/TrialDocumentPicker";
import InstructionChain from "../components/settings/InstructionChain";
import { embedRolePrompts } from "../components/settings/rolePrompts";
import { useEmbedPrompts } from "../components/settings/useEmbedPrompts";
import { MAX_INSTRUCTIONS, TRIAL_POLL_MS, formatUsd, originLabel, subjectOptions, type SubjectOption } from "../components/trials/trialLogic";
import {
  MAX_OBJECTIVE_TRIAL_CHAPTERS,
  buildObjectiveOverride,
  chapterSelectionError,
  estimateSentence,
  formFromResolved,
  isActive,
  objectiveTrialStatus,
  type ObjectiveTrialForm,
} from "../components/objectiveTrials/objectiveTrialLogic";
import { declaredModelNames } from "../components/settings/llmCatalog";

/** Recalcul de l'estimation après la dernière frappe dans la consigne. */
const ESTIMATE_DEBOUNCE_MS = 600;

const when = (iso: string) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

const Section: React.FC<{ step: number; title: string; hint?: string; children: React.ReactNode }> = ({ step, title, hint, children }) => (
  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
    <Typography variant="h5">
      {step}. {title}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.5 }}>
        {hint}
      </Typography>
    )}
    <Box sx={{ mt: hint ? 0 : 1.5 }}>{children}</Box>
  </Box>
);

/**
 * Essais d'objectifs : « ce réglage donne-t-il de meilleurs objectifs, et que
 * ferait-il perdre aux élèves ? ». Une matière, 1 à 3 chapitres, un autre
 * modèle ou une consigne pour l'essai seulement ; le coût estimé ; puis le
 * face-à-face avec les objectifs servis, avant d'appliquer ou de jeter.
 */
const ObjectiveTrialsPage: React.FC = () => {
  const { getCurriculumOverview } = useCurriculumApi();
  const { getLlmCatalog } = useAppConfigsApi();
  const { estimateObjectiveTrial, createObjectiveTrial, listObjectiveTrials } = useObjectiveTrialsApi();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [programs, setPrograms] = React.useState<CurriculumProgram[] | null>(null);
  const [models, setModels] = React.useState<string[]>([]);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);
  const loadCatalog = React.useCallback(async () => {
    setCatalogError(null);
    try {
      const [{ data }, catalog] = await Promise.all([
        getCurriculumOverview(),
        // Sans catalogue, la saisie reste libre : l'estimation refusera un modèle non déclaré ou non tarifé.
        getLlmCatalog().catch(() => null),
      ]);
      setPrograms(data.programs ?? []);
      // Le fournisseur de l'essai est celui de la matière : on propose les
      // modèles déclarés chez les fournisseurs actifs.
      setModels(catalog ? declaredModelNames({ providers: catalog.data.providers, models: catalog.data.models }) : []);
    } catch (e) {
      setCatalogError(errorMessage(e, "Impossible de charger le référentiel."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);
  const subjectNames = React.useMemo(
    () => new Map((programs ? subjectOptions(programs) : []).map((s) => [s.id, s.name])),
    [programs],
  );

  const [trials, setTrials] = React.useState<ObjectiveTrial[] | null>(null);
  const [trialsError, setTrialsError] = React.useState<string | null>(null);
  const loadTrials = React.useCallback(async () => {
    try {
      const { data } = await listObjectiveTrials();
      setTrials(data.trials ?? []);
      setTrialsError(null);
    } catch (e) {
      setTrialsError(errorMessage(e, "Impossible de charger les essais d'objectifs."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    void loadTrials();
  }, [loadTrials]);
  const active = Boolean(trials?.some((t) => isActive(t.status)));
  React.useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => void loadTrials(), TRIAL_POLL_MS);
    return () => window.clearInterval(id);
  }, [active, loadTrials]);

  const basePrompts = useEmbedPrompts();
  const prompts = embedRolePrompts("objective_extraction", basePrompts.prompts, basePrompts.error);

  const [subject, setSubject] = React.useState<SubjectOption | null>(null);
  const [selected, setSelected] = React.useState<UUID[]>([]);
  const [base, setBase] = React.useState<ResolvedObjectiveRole | undefined>(undefined);
  const [form, setForm] = React.useState<ObjectiveTrialForm>({ model: "", instructions: "" });
  const [estimate, setEstimate] = React.useState<{ key: string; data: ObjectiveTrialEstimate } | null>(null);
  const [estimating, setEstimating] = React.useState(false);
  const [estimateError, setEstimateError] = React.useState<string | null>(null);
  const [launching, setLaunching] = React.useState(false);
  const seq = React.useRef(0);

  const changeSubject = (s: SubjectOption | null) => {
    seq.current += 1;
    setSubject(s);
    setSelected([]);
    setBase(undefined);
    setForm({ model: "", instructions: "" });
    setEstimate(null);
    setEstimateError(null);
  };

  const override = React.useMemo(() => (base ? buildObjectiveOverride(form, base) : {}), [form, base]);
  const key = `${selected.join(",")}|${JSON.stringify(override)}`;
  const pickError = selected.length ? chapterSelectionError(selected.length) : null;

  // Première estimation (surcharge vide) : préremplit le formulaire avec le
  // réglage de la matière. Les suivantes suivent chaque modification.
  React.useEffect(() => {
    if (!selected.length || pickError || estimate?.key === key) {
      seq.current += 1;
      setEstimating(false);
      return;
    }
    const mine = ++seq.current;
    setEstimating(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await estimateObjectiveTrial({ document_ids: selected, override });
        if (mine !== seq.current) return;
        if (!base) {
          setBase(data.resolved.objective_extraction);
          setForm(formFromResolved(data.resolved.objective_extraction));
        }
        setEstimate({ key, data });
        setEstimateError(null);
      } catch (e) {
        if (mine === seq.current) setEstimateError(errorMessage(e, "Estimation impossible."));
      } finally {
        if (mine === seq.current) setEstimating(false);
      }
    }, base ? ESTIMATE_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const fresh = Boolean(estimate && estimate.key === key && !estimating);
  const resolved = estimate?.data.resolved.objective_extraction;

  const launch = async () => {
    if (!estimate || !fresh) return;
    const e = estimate.data;
    const served = e.chapters.reduce((n, c) => n + c.served_objectives, 0);
    const ok = await confirm({
      title: "Lancer l'essai ?",
      message:
        `${e.chapters.length} chapitre${e.chapters.length > 1 ? "s" : ""}, ${estimateSentence(e)}.\n\n` +
        `Les ${served} objectifs servis ne changent pas : l'essai rédige un brouillon, que vous comparerez avant d'appliquer ou de jeter.`,
      confirmLabel: "Lancer",
    });
    if (!ok) return;
    setLaunching(true);
    try {
      const { data } = await createObjectiveTrial({ document_ids: selected, override });
      toast.success("Essai lancé.");
      navigate(`/objective-trials/${data.trial.id}`);
    } catch (err) {
      toast.error(errorMessage(err, "Impossible de lancer l'essai."));
      void loadTrials();
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <PageHeader
        title="Essais d'objectifs"
        subtitle="Essayer un autre modèle ou une consigne pour les objectifs sur 1 à 3 chapitres, comparer aux objectifs servis — repris, nouveaux, et ceux qui feraient perdre de la progression —, puis appliquer ou jeter. Rien ne change pour les élèves avant « Appliquer »."
      />

      {catalogError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void loadCatalog()}>
              Réessayer
            </Button>
          }
        >
          {catalogError}
        </Alert>
      )}

      <Card>
        <Section step={1} title="Chapitres" hint={`Une matière, puis 1 à ${MAX_OBJECTIVE_TRIAL_CHAPTERS} de ses chapitres importés.`}>
          {programs ? (
            <TrialDocumentPicker
              programs={programs}
              subject={subject}
              onSubject={changeSubject}
              selected={selected}
              onSelected={setSelected}
              disabled={launching}
              max={MAX_OBJECTIVE_TRIAL_CHAPTERS}
              chaptersOnly
            />
          ) : (
            !catalogError && <Skeleton height={40} sx={{ maxWidth: 420 }} />
          )}
          {pickError && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {pickError}
            </Alert>
          )}
        </Section>

        <Divider />
        <Section
          step={2}
          title="Réglage de l'essai"
          hint="Prérempli avec le réglage des objectifs de la matière. Dans l'ordre où le modèle les reçoit : le prompt de base, la consigne héritée, puis la consigne de l'essai qui la remplace si vous la remplissez. Seuls les champs changés valent pour l'essai, et pour lui seul. Les faits ne sont pas essayés ici : ils seront extraits à l'application, avec les réglages de la matière."
        >
          {base ? (
            <Stack spacing={2} sx={{ maxWidth: 720 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2">Modèle</Typography>
                  <StatusPill tone="neutral">{originLabel(resolved?.origin.model ?? base.origin.model)}</StatusPill>
                </Stack>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={models}
                  value={form.model}
                  disabled={launching}
                  onInputChange={(_, v) => setForm((f) => ({ ...f, model: v }))}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      placeholder={base.model}
                      helperText="Vide = le modèle de la matière. Température 0 : les modèles de raisonnement sont refusés."
                      inputProps={{ ...p.inputProps, "aria-label": "Modèle des objectifs pour l'essai" }}
                    />
                  )}
                />
              </Box>
              {/* Le prompt de base, la consigne héritée, puis celle de l'essai : l'ordre où le modèle les reçoit. */}
              <InstructionChain
                prompts={prompts}
                inherited={base.instructions}
                inheritedOrigin={base.origin.instructions}
                replaced={override.objective_extraction?.instructions !== undefined}
              >
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  disabled={launching}
                  value={form.instructions}
                  onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
                  placeholder={base.instructions ? "Vide : la consigne héritée part telle quelle." : "Vide : le prompt de base seul."}
                  inputProps={{ maxLength: MAX_INSTRUCTIONS, dir: "auto", "aria-label": "Consigne des objectifs pour l'essai" }}
                  helperText={
                    `${form.instructions.length} / ${MAX_INSTRUCTIONS} · remplie, elle REMPLACE la consigne héritée (elles ne s'additionnent pas) ` +
                    "et part seule à la fin du message d'extraction de chaque leçon. Vide = la consigne héritée."
                  }
                />
                {base.instructions && (
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    disabled={launching || form.instructions.trim() === base.instructions.trim()}
                    onClick={() => setForm((f) => ({ ...f, instructions: base.instructions ?? "" }))}
                    sx={{ mt: 0.5 }}
                  >
                    Partir de la consigne héritée
                  </Button>
                )}
              </InstructionChain>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {!selected.length
                ? "Choisissez d'abord des chapitres."
                : estimateError
                  ? "Réglage de la matière illisible : voir l'erreur de l'estimation ci-dessous."
                  : "Lecture du réglage de la matière…"}
            </Typography>
          )}
        </Section>

        <Divider />
        <Section step={3} title="Coût estimé">
          {!selected.length ? (
            <Typography variant="body2" color="text.secondary">
              L'estimation apparaît dès qu'un chapitre est choisi.
            </Typography>
          ) : estimateError ? (
            <Alert severity="error">{estimateError}</Alert>
          ) : !estimate ? (
            <LinearProgress sx={{ maxWidth: 420 }} />
          ) : (
            <Stack spacing={0.75} sx={{ opacity: fresh ? 1 : 0.6 }}>
              <Typography variant="body1">
                <b>Essai :</b> {estimateSentence(estimate.data)}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mesuré sur {estimate.data.window_days} jours (extraction, relecture et appariement des objectifs, rapportés aux leçons
                traitées). Un plancher : une nouvelle tentative de plus n'est pas prévisible.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Si vous appliquez</b>, les faits de chaque objectif écrit sont ré-extraits comme après un import :{" "}
                {estimate.data.facts_per_objective_usd === null
                  ? "coût non chiffrable"
                  : `≈ ${formatUsd(estimate.data.facts_per_objective_usd)} par objectif`}{" "}
                (aujourd'hui {estimate.data.chapters.reduce((n, c) => n + c.served_objectives, 0)} objectifs servis dans ces chapitres).
              </Typography>
            </Stack>
          )}
        </Section>

        <Divider />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="flex-end"
          sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}
        >
          {active && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Un essai d'objectifs tourne ou s'applique déjà : un seul à la fois.
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!fresh || Boolean(pickError) || active || launching || estimate?.data.lessons === 0}
            onClick={() => void launch()}
          >
            {launching ? "Lancement…" : "Lancer l'essai"}
          </Button>
        </Stack>
      </Card>

      <Card sx={{ mt: 2 }}>
        <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 1 }}>
          <Typography variant="h5">Essais d'objectifs</Typography>
          <Typography variant="body2" color="text.secondary">
            Les 50 derniers. Ouvrez un essai pour le comparer aux objectifs servis.
          </Typography>
        </Box>
        {trialsError && (
          <Alert
            severity="error"
            sx={{ mx: 2, mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void loadTrials()}>
                Réessayer
              </Button>
            }
          >
            {trialsError}
          </Alert>
        )}
        {!trials ? (
          !trialsError && (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Box>
          )
        ) : trials.length === 0 ? (
          <EmptyState
            dense
            icon={<FlagIcon />}
            title="Aucun essai d'objectifs pour l'instant"
            description="Choisissez une matière et quelques chapitres ci-dessus, réglez l'essai, puis lancez-le."
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lancé le</TableCell>
                  <TableCell>Matière</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Chapitres</TableCell>
                  <TableCell>Modèle</TableCell>
                  <TableCell align="right">Estimé</TableCell>
                  <TableCell align="right">Réel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trials.map((t) => {
                  const s = objectiveTrialStatus(t.status);
                  return (
                    <TableRow key={t.id} hover onClick={() => navigate(`/objective-trials/${t.id}`)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{when(t.created_at)}</TableCell>
                      <TableCell>{(t.subject_id && subjectNames.get(t.subject_id)) || "—"}</TableCell>
                      <TableCell>
                        <StatusPill tone={s.tone}>{s.label}</StatusPill>
                      </TableCell>
                      <TableCell>
                        {t.documents_done} / {t.document_ids.length}
                      </TableCell>
                      <TableCell className="mono" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                        {t.resolved.objective_extraction?.model ?? "—"}
                        {t.resolved.objective_extraction?.instructions ? " · consigne" : ""}
                      </TableCell>
                      <TableCell align="right">{formatUsd(t.estimate_usd)}</TableCell>
                      <TableCell align="right">{formatUsd(t.actual_usd)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default ObjectiveTrialsPage;
